/**
 * The club's optional order-received notice — an extra line clubs write for
 * fans on the order confirmation screen ("Keep an eye out for your order!").
 *
 * Two sources feed it, and which one wins matters:
 *  - `club_settings.order_received_notice` is the ON/OFF switch and the source
 *    text the club typed.
 *  - `club_translations` under ORDER_RECEIVED_NOTICE_KEY holds the per-locale
 *    wording, written by hand or by Auto-Translate.
 *
 * Kept out of TagPage so the precedence rules below are testable without
 * rendering the whole fan page — same reason buildFanPalette lives in
 * src/lib/fanTheme.ts.
 */

export const ORDER_RECEIVED_NOTICE_KEY = 'ordering.orderReceivedNotice';

/**
 * Resolves what the fan should actually see, or '' for "render nothing".
 *
 * The raw column gates everything: a club that clears the notice must see it
 * disappear even if translation rows survive somewhere, so a leftover row can
 * never resurrect it.
 *
 * When the notice IS set, a translation only changes the wording. `t` returns
 * the literal key for a key with no translation row and no DEFAULT_TRANSLATIONS
 * entry (useTranslation.ts: `translations[key] || DEFAULT_TRANSLATIONS[key] ||
 * params?.defaultValue || key`), and this key deliberately has no default — so
 * `translated === key` is the sentinel for "untranslated in this locale", and
 * we fall back to the club's source text rather than printing the key.
 */
export function resolveOrderReceivedNotice(
  raw: string | null | undefined,
  t: (key: string) => string,
): string {
  const base = (raw ?? '').trim();
  if (!base) return '';

  const translated = t(ORDER_RECEIVED_NOTICE_KEY);
  if (translated === ORDER_RECEIVED_NOTICE_KEY) return base;

  // A blank translation is treated as "not translated", not as "hide it" —
  // the column already decided the notice should show.
  return translated.trim() || base;
}
